import React from "react";
import { SearchPanelProps } from "./SearchPanel.types";

export function useSearchPanel<T>(props: SearchPanelProps<T>) {
  const {
    ContentComponent,
    HeaderComponent,
    MoreDialogComponent,
    data,
    emptyStateMessage,
    emptyStateTitle,
    hideSearchOnSelect = true,
    idKey,
    isLoading,
    itemsPerPage,
    itemsPerPageOptions,
    moreActions,
    onDeselectAll,
    onItemsPerPageChange,
    onPageChange,
    onSearch,
    onSort,
    onTagDelete,
    onToggleSelect,
    onToggleSelectAll,
    page,
    search,
    selectable,
    selected = [],
    selectedDisabled,
    selectionDisabled,
    sort,
    sortOptions,
    sortWidth,
    tags,
    totalCount,
    ...boxProps
  } = props;

  const [moreOpen, setMoreOpen] = React.useState(false);

  const isSelected = React.useCallback(
    (item: T) => {
      return selected.some((i) => i[idKey] === item[idKey]);
    },
    [selected, idKey]
  );

  const isAllSelected = React.useCallback(
    (items?: T[]) => {
      if (!items || items.length === 0) {
        return false;
      }
      return items.every((item) =>
        selected.some((i) => i[idKey] === item[idKey])
      );
    },
    [selected, idKey]
  );

  const openMore = React.useCallback(() => {
    setMoreOpen(true);
  }, []);

  const closeMore = React.useCallback(() => {
    setMoreOpen(false);
  }, []);

  const allSelected = React.useMemo(
    () => !isLoading && isAllSelected(data),
    [data, isAllSelected, isLoading]
  );

  const selectedIds = React.useMemo(() => {
    return selected.map((item) => item[idKey] as unknown as string);
  }, [selected, idKey]);

  return {
    ContentComponent,
    HeaderComponent,
    MoreDialogComponent,
    allSelected,
    boxProps,
    closeMore,
    data,
    emptyStateMessage,
    emptyStateTitle,
    hideSearchOnSelect,
    idKey,
    isLoading,
    isSelected,
    itemsPerPage,
    itemsPerPageOptions,
    moreActions,
    moreOpen,
    onDeselectAll,
    onItemsPerPageChange,
    onPageChange,
    onSearch,
    onSort,
    onTagDelete,
    onToggleSelect,
    onToggleSelectAll,
    openMore,
    page,
    search,
    selectable,
    selected,
    selectedDisabled,
    selectedIds,
    selectionDisabled,
    sort,
    sortOptions,
    sortWidth,
    tags,
    totalCount,
  };
}
